import React, { PropsWithChildren, useState } from 'react';
import { EntityNameColumn } from '@components/entity-name-column';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import { Avatar, Chip, Divider, Tooltip, Typography } from '@material-ui/core';
import { useMemo } from 'react';
import CategoryIcon from '@material-ui/icons/Category';
import LanguageIcon from '@material-ui/icons/Language';

import { EntityIcon } from '@components/entity-type-icon';
import { EntityType } from '@lib/deals';
import { ListItem, ListItemAvatar, ListItemText } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import type { UseFormMethods as UseFormReturn } from 'react-hook-form';

type EnumObject = { label: React.ReactNode; value: any };
type EnumMap = EnumObject[];

/**
 * Create a small subtitle, used for Entity Names.
 */
function SmallSubTitle({ name, icon, display = 'block' }) {
  return (
    <span style={{ color: 'inherit', fontSize: 11, display, alignItems: 'center' }}>
      {icon}&nbsp;{name}
    </span>
  );
}

/**
 * Gets the company details from the server and shows the
 * country and the primary type of the company.
 */
function ShowCompanyDetails({companyData}) {
  const companyType = companyData && companyData.company_types[0]?.name;
   const parsedBadges = useMemo(() => parseBadgeRanges(companyData?.badges || []), [companyData?.badges]);
    const [currentScore, setCurrentScore] = useState<number>(companyData?.reputation_score);
    const currentBadge = useMemo(
      () => getBadgeByScore(currentScore, parsedBadges),
      [currentScore, parsedBadges]
    );
  return (
    <>
      {companyData && (
        <>
        {companyData.company_active && (
              <Chip
                label={companyData.company_active}
                size="small"
                style={{ marginRight: 10, backgroundColor:   companyData.company_active === "Active" ? "#b71c1c" : "#080e94",color:"#fff" }}
              />
            )}
        {companyData?.reputation_score && (
             <Tooltip  title={
                    <>
                      Reputation Score: {companyData?.reputation_score}
                      <br />
                      Range: {currentBadge?.range?.replace('TO', ' – ')}
                    </>
                  }>
               <Chip
                 label={currentBadge?.name || "N/A"}
                size="small"
                style={{ marginRight: 10, backgroundColor:  "#6a0f78",color:"#fff" }}
              />
              </Tooltip>
            )}
          <SmallSubTitle
            name={companyData.hqcountry}
            icon={<LanguageIcon style={{ fontSize: '1em' }} />}
          />
          {companyType && (
            <SmallSubTitle
              name={companyType}
              icon={<CategoryIcon style={{ fontSize: '1em' }} color="primary" />}
            />
          )}
        </>
      )}
    </>
  );
}

export const CompanyEdit = ({
  name: entityName,
  vtrId: entityVtrId,
  type: entityType,
  white = false,
  avatarImg = null,
  companyData = null,
}: {
  name: React.ReactNode;
  vtrId?: string;
  type: string;
  white?: boolean;
  avatarImg?: string;
  companyData?: Object;
}) => {
  const secondaryProps = useMemo(() => (white ? { color: 'inherit' } : {}), [white]);
  let Details: any;
  if (entityType === EntityType.company) {
    Details = ShowCompanyDetails;
  }
  return entityName || entityVtrId ? (
    <>
      <EntityNameColumn
        name={entityName}
        type={entityType}
        vtrId={entityVtrId}
        secondary={
          entityVtrId ? (
            Details ? (
              <Details companyData={companyData} />
            ) : (
              <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
            )
          ) : null
        }
        secondaryTypographyProps={secondaryProps}
        avatarImg={avatarImg}
      />
    </>
  ) : null;
};

const smallAvatar = { height: '1.1em', width: '1.1em' };

export const EmptyEntityNameColumn = ({
  type,
  name,
  secondary = null,
  iconProps = {},
  hideTooltip = false,
  secondaryTypographyProps = {},
}) => {
  const entity = (
    <ListItem style={{ padding: 0 }} component="div">
      <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
        <EntityIcon noTooltip type={type as EntityType} {...iconProps} disabled />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="body2" style={{ fontStyle: 'italic' }}>
            Select a content
          </Typography>
        }
        secondary={secondary}
        secondaryTypographyProps={secondaryTypographyProps}
      />
    </ListItem>
  );
  return hideTooltip ? entity : <Tooltip title={type}>{entity}</Tooltip>;
};

// utils/preFillForm to fill the AI response in company form
export const preFillForm_Company = (
  form: any,
  prefillData: any,
  enqueueSnackbar: (msg: string, options?: any) => void
) => {
  try {
    if (!prefillData) return;
    // ✅ Map basic fields with ?? and ?.
    form.setValue('vtr_company_name', prefillData?.['Company Name'] ?? '');
    form.setValue('company_display_name', prefillData?.['Company Name'] ?? '');
    form.setValue('company_description', prefillData?.['Company Description'] ?? '');
    form.setValue('company_aka', prefillData?.['Also Known As']?.join(', ') ?? '');
    form.setValue('company_keywords', prefillData?.['Keywords']?.join(', ') ?? '');
    form.setValue('company_services', prefillData?.['Services Offered']?.join(', ') ?? '');
    form.setValue('website', prefillData?.['Website'] ?? '');
    form.setValue('hqaddress', prefillData?.['Location Headquarters'] ?? '');
    form.setValue('hqLocation', prefillData?.['Location Headquarters'] ?? '');
    form.setValue('company_size', prefillData?.['Team Size'] ?? '');
    form.setValue('company_types', prefillData?.['Company Type'] ?? '');
    form.setValue('company_founding_year', prefillData?.['Founding Year'] ?? '');
    form.setValue('logo_url', prefillData?.['Logo Url'] ?? '');
  //  form.setValue('categories', prefillData?.['Key Areas'] ? [prefillData['Key Areas']] : []);
    form.setValue('source_data_name', prefillData?.['Source Url'] ?? '');
    form.setValue('company_publish_ready', '0');
    form.setValue('data_owner', 'DELI');

    // ✅ Parent company standalone logic
    const parentCompany = prefillData?.['Parent Company'];
    if (parentCompany) {
      form.setValue(
        'standalone_company',
        parentCompany.toLowerCase() === 'independent' ? 'Yes' : 'No'
      );
    }

    // ✅ Associated persons
    const associatedPersons = Array.isArray(prefillData?.['Leadership Contacts'])
      ? prefillData['Leadership Contacts'].map((p: any) => ({
          name: p?.Name ?? '',
          designation: p?.Designation ?? '',
          email: p?.Email ?? '',
          linkedin: p?.['LinkedIn Url'] ?? '',
        }))
      : [];
    // form.setValue('associated_person', associatedPersons);

    const clientServices = Array.isArray(prefillData?.['Services Offered'])
  ? prefillData['Services Offered']
      .filter(item => item) // remove null/undefined
      .map((item: any) => {
        if (typeof item === 'object') {
          return {
            id: item.id,                 // real ID
            service: item.service,       // real service name
            attributes_count: item.attributes_count ?? null
          };
        }

        // Fallback for string values
        return {
          id: null,
          service: String(item),
          attributes_count: null
        };
      })
  : [];
form.setValue('clientServices', clientServices);
    // ✅ Client services
   const companyTypes = Array.isArray(prefillData?.['Company Type'])
  ? prefillData['Company Type']
      .filter((s: any) => typeof s === 'object' && s.type?.trim() !== '') 
      .map((obj: any) => ({
        id: obj.id,            // use the real ID
        label: obj.type.trim(), // use the type as label
        value: obj.id          // value should also be the REAL ID
      }))
  : [];
    if (companyTypes.length > 0) {
      form.setValue('categories', companyTypes);
    }

    // ✅ Social links
    const socialLinks = prefillData?.['Social Links'] ?? {};
    form.setValue('social_linkedin_url', socialLinks?.LinkedIn ?? '');
    form.setValue('social_facebook_url', socialLinks?.Facebook ?? '');

    const socialUrls = Object.values(socialLinks).filter(Boolean) as string[];
    form.setValue('social_urls', socialUrls);

    /*   // ✅ Other offices
    const otherOffices = prefillData?.['Other Offices'];
    form.setValue(
      'other_office_locations',
      Array.isArray(otherOffices) ? otherOffices : otherOffices ? [otherOffices] : []
    ); */
    // ✅ Other offices normalization
    const otherOfficesRaw = prefillData?.['Other Offices'];

    // Convert string | array into structured array
    const normalizedOtherOffices = (() => {
      if (!otherOfficesRaw || otherOfficesRaw === 'Not Available') return [];

      const offices = Array.isArray(otherOfficesRaw) ? otherOfficesRaw : [otherOfficesRaw];

      return offices
        .map((office: string) => {
          const parts = office
            .split(',')
            .map(p => p.trim())
            .filter(Boolean);
          const [city = '', state = '', country = ''] =
            parts.length === 3
              ? parts
              : parts.length === 2
              ? [parts[0], '', parts[1]]
              : ['', '', parts[0] || ''];

          return {
            city,
            state,
            country,
            lat: null,
            lng: null,
          };
        })
        .filter(({ city, country }) => city || country);
    })();

    form.setValue('otherOffices', normalizedOtherOffices);
    // ✅ Contact details
    form.setValue('email', prefillData?.['Contact Details']?.Email ?? '');
    form.setValue('phone', prefillData?.['Contact Details']?.Phone ?? '');
    const compliance = prefillData?.['Compliance and Operational Data'] ?? {};

    // 🧩 Flattened Compliance fields for direct form prefilling
    form.setValue('security_practices', compliance?.['Security Practices'] ?? '');
    form.setValue('diversity_and_inclusion', compliance?.['DE&I'] ?? '');
    form.setValue(
      'environment_sustainability_goals',
      compliance?.['Environment Sustainability Goals'] ?? ''
    );
    form.setValue('tax_credits_or_incentives', compliance?.['Tax Credits/Incentives'] ?? '');
    form.setValue('commercial_model_and_pricing', compliance?.['Commercial Model/Rate Card'] ?? '');
  } catch (error) {
    enqueueSnackbar('Error pre-filling form. Please fill manually.', { variant: 'warning' });
  }
};

// ✅ List of invalid values (common constant)
const invalidValues = [  'Not specified',  'N/A',  null,  undefined,  '',  'not specified',  'n/a',  'not mentioned',  'not available',  'Not Available',];

// ✅ Helper to map company data into normalized structure
export function normalizeCompanyData(company: any, url: string) {
  return {
    'Company Name': company?.['Company Name'] ?? '',
    'Also Known As': company?.['Also Known As'] ?? [],
    'Company Description': company?.['Company Description'] ?? '',
    'Company Type': company?.['Company Type'] ?? '',
    'Services Offered': company?.['Services Offered'] ?? [],
    'Location Headquarters': company?.['Location Headquarters'] ?? '',
    'Other Offices': company?.['Other Offices'] ?? '',
    'Team Size': company?.['Team Size'] ?? '',
    'Founding Year': company?.['Founding Year'] ?? '',
    'Parent Company': company?.['Parent Company'] ?? '',
    'Leadership Contacts': company?.['Leadership Contacts'] ?? [],
    'Social Links': company?.['Social Links'] ?? {},
    Website: company?.['Website'] ?? '',
    Badges: company?.['Badges'] ?? [],
    'Contact Details': company?.['Contact Details'] ?? {},
    'Clients Or Projects': company?.['Clients Or Projects'] ?? [],
    Keywords: company?.['Keywords'] ?? [],
    'Compliance and Operational Data': {
      'Security Practices':
        company?.['Compliance_and_Operational_Data']?.['Security Practices'] ?? '',
      'DE&I': company?.['Compliance_and_Operational_Data']?.['DE&I'] ?? '',
      'Environment Sustainability Goals':
        company?.['Compliance_and_Operational_Data']?.['Environment Sustainability Goals'] ?? '',
      'Tax Credits/Incentives':
        company?.['Compliance_and_Operational_Data']?.['Tax Credits/Incentives'] ?? '',
      'Commercial Model/Rate Card':
        company?.['Compliance_and_Operational_Data']?.['Commercial Model/Rate Card'] ?? '',
    },
    'Source Url': url?.trim?.() ?? '',
  };
}

// ✅ Recursively clean object: remove invalid values, empty [] and {}
export function cleanInvalidData(obj: any): any {
  if (Array.isArray(obj)) {
    return obj
      .map(item => cleanInvalidData(item))
      .filter(
        item =>
          !invalidValues.includes(item) && // 🚫 filter invalid values
          item !== null &&
          !(Array.isArray(item) && item.length === 0) &&
          !(typeof item === 'object' && Object.keys(item).length === 0)
      );
  } else if (typeof obj === 'object' && obj !== null) {
    const cleaned: any = {};
    for (const key in obj) {
      const value = cleanInvalidData(obj[key]);
      if (
        !invalidValues.includes(value) &&
        !(Array.isArray(value) && value.length === 0) &&
        !(typeof value === 'object' && Object.keys(value).length === 0)
      ) {
        cleaned[key] = value;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : null;
  }
  return obj;
}

export function filterClientServices(
  clientServices: (string | { service: string })[] = [],
  masterServices: { id?: number; service: string }[] = []
) {
  if (!clientServices.length || !masterServices.length) {
    console.warn('No client or master services available.');
    return [];
  }

  // Normalize client service names
  const normalizedClient = clientServices
    .map(cs =>
      (typeof cs === 'string' ? cs : cs.service)?.trim().toLowerCase()
    )
    .filter(Boolean);

  // Exact match: master.service === client service
  const matched = masterServices.filter(ms => {
    const masterName = ms.service?.trim().toLowerCase();

    return normalizedClient.some(clientName => clientName === masterName);
  });

  return matched;
}


// ✅ Filter company types and return the full master object on exact match
export function filterCompanyTypes(
  companyType: string = '',
  masterTypes: { id: number; type: string }[] = []
) {
  if (!companyType || !masterTypes.length) {
    console.warn('No client or master services available.');
    return [];
  }

  const normalizedCompanyType = companyType.trim().toLowerCase();

  // Find exact match in master types
  const match = masterTypes.find(
    (m) => m.type?.trim().toLowerCase() === normalizedCompanyType
  );

  // Return the full matched master object
  return match ? [match] : [];
}

import * as qpService from '@api/quotes-policies';
import { getBadgeByScore, parseBadgeRanges } from '@pages/company-reputation-score/Tabs/CompanyReputationTab/BadgeConfig';
import { getServiceHierarchy } from '@api/company';

export function getAllQuotesAndPoliciesByVtrId(vtrId: string) {
  return qpService.filter({
    page: 1,
    pageSize: 0,
    filters: [
      {
        id: 'vtr_id',
        value: { $eq: vtrId },
      },
    ],
  });
}
import { map } from 'rxjs/operators';
import { ServiceHierarchyResponse, ServiceHierarchyItem } from './form/company.types';

export function getCompanyServices(
  serviceIdsParam: string,
  sortedServices: any[]
) {
  return getServiceHierarchy(serviceIdsParam).pipe(
    map((response: ServiceHierarchyResponse) => {
      const hierarchyMap = new Map<number, ServiceHierarchyItem>(
        response.data.map(item => [item.service_id, item])
      );

      return sortedServices.map(service => ({
        ...service,
        hierarchy_level:
          hierarchyMap.get(service.id)?.hierarchy_level ?? null,
        ultimate_parent:
          hierarchyMap.get(service.id)?.ultimate_parent ?? null,
      }));
    })
  );
}
export const getUnlockedSections = (
   sections: any,
   isEdit: boolean
 ) => {
   const sectionLabels: Record<string, string> = {
     associated_person_section: 'Associated Persons',
     attribute_section: 'Attributes',
     badges_section: 'Badges',
     contact_section: 'Contact',
     metadata_section: 'Metadata',
     profile_section: 'Profile',
     services_section: 'Services',
     social_section: 'Social',
     types_section: 'Company Types',
     media_section: 'Media',
     policies_section: 'Policies',
   };
 
   const allRequiredSections = [
     'associated_person_section',
     'attribute_section',
     'badges_section',
     'contact_section',
     'metadata_section',
     'profile_section',
     'services_section',
     'social_section',
     'types_section',
     'policies_section',
   ];
 
   const isSectionLocked = (section: any) => {
     if (!section) return false;
 
     if ('isLockApplied' in section) {
       return section.isLockApplied === true;
     }
 
     return section.verified === true;
   };
 
   const sectionsToValidate = isEdit
     ? Object.keys(sections || {}) // only existing sections
     : allRequiredSections; // all required for create
 
   return sectionsToValidate
     .filter(key => !isSectionLocked(sections?.[key]))
     .map(key => sectionLabels[key] || key);
	 };